import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router";
import { Lead } from "shared/topchef.types";
import { useBackofficeApi } from 'shared/useBackofficeApi';
import { useTranslation } from "react-i18next";

import { EVENT_TYPES, EventType, Region, REGIONS, LEAD_STATUS, LeadStatus } from 'shared/topchef.types'


const EditLead = () => {
  const { t } = useTranslation();
  const { leadId } = useParams<{ leadId: string }>();
  const navigate = useNavigate();
  const backofficeApi = useBackofficeApi();
  const isFrenchExperience = backofficeApi.country.toLowerCase() === 'france';

  const [lead, setLead] = useState<Lead | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!leadId) return;

    setLoading(true);
    setError(null);

    backofficeApi.loadLeadDetails(leadId)
      .then(setLead)
      .catch(_err => setError(t('apiErrors.loadLead', { details: _err.message }) as string))
      .finally(() => setLoading(false));
  }, [leadId, backofficeApi]); // eslint-disable-line react-hooks/exhaustive-deps


  const handleInputEventChange = (
  e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
) => {
  if (!lead) return;
  const { name, value } = e.target;
  setLead({
    ...lead,
    [name]: value,
  });
};

const handleTelInputEventChange = (e: React.ChangeEvent<HTMLInputElement>) => {
  if (!lead) return;
  const { name, value } = e.target;
  const isValid = /^[0-9\s\-()+]*$/.test(value);
  if (isValid) {
    setLead({
      ...lead,
      [name]: value,
    });
  }
};

  function tomorrow(): string {
    const now = new Date();
    const tomorrow = Date.UTC(now.getFullYear(), now.getMonth(), now.getDate() + 1, 0, 0, 0);
    const formattedTomorrow = new Date(tomorrow).toISOString().split("T")[0];
    return formattedTomorrow
  }

const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    
    if (!lead || !lead.id) return; // on vérifie qu'on a bien un lead avec ID

    console.log("Données envoyées :", lead);

    try {
      // Envoi des données modifiées au backend
      await backofficeApi.loadUpdatedLead(lead.id, lead); 
      // Redirection après succès
      navigate(`/backoffice/leads/all/${lead.id}`);
    } catch (error) {
      console.error("Erreur lors de la mise à jour du lead", error);
      alert("Erreur lors de la mise à jour du lead");
    }
};


  if (loading) return <div>Chargement...</div>;
  if (error) return <div>{error}</div>;
  if (!lead) return <div>Aucun brief trouvé.</div>;

  return (
        <div className="container p-2 col-md-6 overflow-auto edit-brief">
                  <h3 className="mb-3 text-center mt-5">Modifier le brief</h3>
                  <form onSubmit={handleSubmit} className="container p-3">      
                        <div id="details_event_form" className="md-3">

                          <div className="mb-3 row">
                            <div className="col-md-6">
                              <label htmlFor="brief_status" className="form-label">Statut du brief</label>
                              <select
                                id="brief_status"
                                name="status"
                                data-testid="status"
                                className="form-select"
                                value={lead.status}
                                onChange={handleInputEventChange}
                                required
                              >
                                <option value="">Sélectionner un statut</option>
                                {LEAD_STATUS.map((leadStatus: LeadStatus) => (
                                  <option key={leadStatus.value} value={leadStatus.value}>
                                    {t(leadStatus.label)}
                                  </option>
                                ))}
                              </select>
                            </div>
                          </div>

                          <div className="mb-3 row">
                              <div className="col-md-6">
                                  <label htmlFor="brief_event_date" className="form-label">Date de l'événement</label>
                                  <input id="brief_event_date" name="targetDate" data-testid="targetDate" type="date" min={tomorrow()} value={lead.targetDate} className="form-control" required onChange={handleInputEventChange} />
                              </div>
                              <div className="col-md-6">
                                  <label htmlFor="event_type" className="form-label">Type d'événement</label>
                                  <select id="event_type" className="form-select" required name="type" data-testid="type" onChange={handleInputEventChange} size={1} value={lead.type!}>
                                      <option value="">Sélectionner le type d'événement</option>
                                      {EVENT_TYPES.map((eventType: EventType) => (
                                        <option key={eventType.value} value={eventType.value} >
                                          {t(eventType.label)}
                                        </option>
                                      ))}
                                  </select>
                              </div>
                          </div>
                          <div className="mb-3 row">
                              <div className="col-md-6">
                                  <label htmlFor="brief_date_starting" className="form-label">Heure de début</label>
                                  <input type="time" id="brief_date_starting" className="form-control" name="startTime" onChange={handleInputEventChange} value={lead.startTime} />
                              </div>
                              <div className="col-md-6">
                                  <label htmlFor="brief_date_ending" className="form-label">Heure de fin</label>
                                  <input type="time" id="brief_date_ending" className="form-control" name="endTime" onChange={handleInputEventChange} value={lead.endTime} />
                              </div>
                          </div>
                          <div className="mb-3 row">
                              <div className="col-md-6">
                                  <label htmlFor="brief_guests" className="form-label">Nombre de personnes</label>
                                  <input type="number" id="brief_guests" data-testid="guests" className="form-control" placeholder="combien d'invités ?" required name="guests" onChange={handleInputEventChange} value={lead.guests} />
                              </div>
                              <div className="col-md-6">
                                  <label htmlFor="brief_budget" className="form-label">Budget total</label>
                                  <input type="number" id="brief_budget" data-testid="budget" min={0} className="form-control" placeholder="Montant HT en €" name="budget" onChange={handleInputEventChange} value={lead.budget} />
                              </div>
                          </div>
                          <div className="mb-3 row">
                              <div className="col-md-6">
                                  <label htmlFor="brief_zipcode" className="form-label">Ville</label>
                                  <input type="text" id="brief_zipcode" data-testid="city" placeholder="Emplacement souhaité de l'événement" aria-label="Emplacement souhaité de l'événement" className="form-control" name="city" onChange={handleInputEventChange} value={lead.city}/>
                              </div>
                              <div className="col-md-6">
                                  {isFrenchExperience && (
                                    <>
                                      <label htmlFor="region" className="form-label">Région</label>
                                      <select id="region" className="form-select" required={isFrenchExperience} name="region" data-testid="region" onChange={handleInputEventChange} size={1} value={lead.region!}>
                                          <option value="">Sélectionnez la région</option>
                                            {REGIONS.map((region: Region) => (
                                              <option key={region.value} value={region.value}>
                                                {region.label}
                                              </option>
                                            ))}
                                      </select>
                                    </>
                                  )}
                              </div>
                          </div>
                          <div className="mb-3">
                              <label htmlFor="event_details_description" className="form-label">Détails de l'événement</label>
                              <textarea className="form-control" id="event_details_description" data-testid="description" rows={9} placeholder="Parlez-nous de l'événement..." required name="description" onChange={handleInputEventChange} value={lead.description!}></textarea>
                          </div>                      
                      </div>
                      <div id="details_contact_form" className="p-3 bg-light mb-3 rounded">
                          <h5 className="mb-3">Informations Client</h5>
                          <div className="row">
                              <div className="col-md-6">
                                  <label htmlFor="brief_company" className="form-label">Entreprise</label>
                                  <input type="text" id="brief_company" data-testid="contactCompany" className="form-control" placeholder="Nom de l'entreprise" required name="contactCompany" onChange={handleInputEventChange} value={lead.contactCompany} />
                                </div>
                                <div className="col-md-6">
                                  <label htmlFor="brief_contact_name" className="form-label">Nom & Prénom du contact</label>
                                  <input type="text" id="brief_contact_name" data-testid="contactName"  className="form-control" placeholder="Nom Prénom" required name="contactName" onChange={handleInputEventChange} value={lead.contactName} />
                              </div>
                          </div>
                          <div className="row my-3">
                              <div className="col-md-6">
                                  <label htmlFor="brief_contact_mail" className="form-label">Email du contact</label>
                                  <input  type="email" id="brief_contact_mail" data-testid="contactEmailAddress" className="form-control" placeholder="votre adresse@email.com" required name="contactEmailAddress" onChange={handleInputEventChange} value={lead.contactEmailAddress} />
                              </div>
                              <div className="col-md-6">
                                  <label htmlFor="brief_contact_tel" className="form-label">Tél. portable</label>
                                  <input type="tel" id="brief_contact_tel" autoComplete="tel" data-testid="contactMobilePhone" className="form-control" pattern="^[0-9\s\-\(\)\+]+$" max="17" maxLength={17} min="10" minLength={10} name="contactMobilePhone" onChange={handleTelInputEventChange} value={lead.contactMobilePhone} />
                              </div>
                          </div>
                          <div className="row">
                              <div className="col-md-6">
                                  <label htmlFor="brief_contact_tel_fix" className="form-label">Tél. fixe</label>
                                  <input type="tel" id="brief_contact_tel_fix" autoComplete="tel" className="form-control" data-testid="contactLandlinePhone" pattern="^[0-9\s\-\(\)\+]+$" max="17" maxLength={17} min="10" minLength={10} placeholder="01..." name="contactLandlinePhone" onChange={handleTelInputEventChange} value={lead.contactLandlinePhone} />
                              </div>
                          </div>
                      </div>

      <button type="submit" className="btn btn-primary save-edited-lead">Enregistrer</button>
    </form>
    </div>
  );
};

export default EditLead;
